<!-- project-detail.component.html -->
<div class="container mx-auto px-4 py-6">
  <!-- Loading State -->
  <div *ngIf="loading" class="flex justify-center items-center h-64">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
  </div>

  <!-- Error State -->
  <div *ngIf="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
    {{ error }}
  </div>

  <!-- Main Content -->
  <div *ngIf="projet && !loading" class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
    <!-- Colonne de gauche - Plan 3D et informations -->
    <div class="md:col-span-1">
      <!-- Plan 3D -->
      <div class="bg-white rounded-[8px] shadow mb-6 overflow-hidden">
        <img [src]="'https://wakana.online/repertoire_chantier/'+ projet.plan" [alt]="'Plan 3D de ' + projet.name"
          class="w-full h-auto rounded-t">

        <div class="p-4">
          <h2 class="text-xl font-bold text-[#1A365D] mb-2">{{ projet.name }}</h2>
          <div class="flex items-center text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1 text-orange-500" viewBox="0 0 20 20"
              fill="currentColor">
              <path fill-rule="evenodd"
                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                clip-rule="evenodd" />
            </svg>
            <span class="text-[#64748B]">{{ projet.address }}</span>
          </div>
        </div>

        <!-- Progression -->
        <div class="bg-[#E8F0FE] rounded-lg shadow p-[15px] mb-6 mx-4">
          <div class="flex items-center mb-2">
            <i class="fa-solid fa-chart-line mr-2"></i>
            <h3 class="text-gray-700 text-xs">{{ t('projectPresentation.progression') }}</h3>
          </div>

          <ng-container *ngIf="!isLoadingProgress; else loadingProgress">
            <div class="flex justify-between items-center mb-2">
              <span class="text-xl font-bold mr-2 ml-2">{{ averageProgress }}%</span>
              <div>
                <i class="fa-solid fa-spinner text-xs text-[#4A6FA5]"></i>
                <span class="px-2 py-1 text-[#4A6FA5] text-xs">{{ statutFrancais }}</span>
              </div>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div [style.width.%]="averageProgress" [style.background]="getGradientBackgroundDetail(averageProgress!)"
                class="h-full rounded-full transition-all duration-300">
              </div>
            </div>
          </ng-container>

          <ng-template #loadingProgress>
            <p class="text-xs text-gray-500">{{ t('projectPresentation.loadingProgress') }}</p>
          </ng-template>
        </div>

        <!-- Informations surface et lots -->
        <div class="grid grid-cols-2 gap-4 mb-6 px-4">
          <div class="bg-gray-50 p-3 rounded shadow-md">
            <div class="flex items-center mb-2">
              <i class="fa-solid fa-ruler-combined fa-rotate-90 mr-2 w-[12px] h-[12px] text-[#64748B]"></i>
              <h3 class="text-[#64748B] text-xs">{{ t('projectPresentation.surface') }}</h3>
            </div>
            <p class="text-sm font-semibold ml-2">{{ projet.area }}m²</p>
          </div>
          <div class="bg-gray-50 p-3 rounded shadow-md">
            <div class="flex items-center mb-2">
              <i class="fa-solid fa-layer-group mr-2 w-[12px] h-[12px] text-[#64748B]"></i>
              <h3 class="text-[#64748B] text-xs">{{ t('projectPresentation.lots') }}</h3>
            </div>
            <p class="font-semibold text-sm">{{ projet.numberOfLots }}</p>
          </div>
        </div>

        <!-- Dates -->
        <div class="grid grid-cols-2 gap-4 mb-6 px-4">
          <div class="bg-gray-50 p-3 rounded shadow-md">
            <div class="flex items-center mb-2">
              <i class="fa-solid fa-calendar-days mr-2 w-[12px] h-[12px] text-[#64748B]"></i>
              <h3 class="text-[#64748B] text-xs">{{ t('projectPresentation.startDate') }}</h3>
            </div>
            <p class="text-sm font-semibold ml-2">{{ dateDebut }}</p>
          </div>
          <div class="bg-gray-50 p-3 rounded shadow-md">
            <div class="flex items-center mb-2">
              <i class="fa-regular fa-calendar-check mr-2 w-[12px] h-[12px] text-[#64748B]"></i>
              <h3 class="text-[#64748B] text-xs">{{ t('projectPresentation.endDate') }}</h3>
            </div>
            <p class="text-sm font-semibold ml-2">{{ dateFinPrevue }}</p>
          </div>
        </div>

        <!-- Budget -->
        <div class="px-4 pb-4 mb-6">
          <div class="flex items-center mb-2">
            <i class="fa-solid fa-wallet mr-2 w-[12px] h-[12px] text-[#64748B]"></i>
            <h3 class="text-[#64748B] text-xs">{{ t('projectPresentation.budget') }}</h3>
            <span class="ml-auto text-xs font-medium text-[#64748B]">
              {{ t('projectPresentation.used') }}:<span class="text-[#FF5C02] font-bold"> {{ progressionBudgetaire
                }}%</span>
            </span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
            <div [style.width.%]="progressionBudgetaire"
              [style.background]="getGradientBackgroundDetail(progressionBudgetaire)"
              class="h-full rounded-full transition-all duration-300">
            </div>
          </div>
          <div class="flex justify-between text-sm text-gray-600">
            <span class="text-sm font-semibold">{{ budgetUtilise | number }} F</span>
            <span class="text-sm font-semibold">{{ budgetTotal | number }} F</span>
          </div>
        </div>

        <!-- Statut avec mise à jour dynamique -->
        <div class="rounded shadow p-4 mx-4 mb-4 bg-white">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-[#64748B] text-xs">{{ t('projectPresentation.status') }}</h3>
            <!-- Indicateur de chargement -->
            <div *ngIf="isUpdatingStatus" class="flex items-center gap-2">
              <div class="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent"></div>
              <span class="text-xs text-gray-500">{{ t('projectPresentation.updating') }}</span>
            </div>
          </div>

          <!-- Message d'erreur si échec -->
          <div *ngIf="statusUpdateError" class="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            {{ statusUpdateError }}
          </div>

          <div class="flex space-x-4">
            <!-- En cours -->
            <label class="flex items-center cursor-pointer" [class.opacity-50]="isUpdatingStatus">
              <input type="radio" name="statut" class="custom-radio cursor-pointer"
                [checked]="isStatutSelected('En cours')" [disabled]="isUpdatingStatus"
                (change)="changeStatut('En cours')">
              <span class="ml-2 text-gray-700 font-bold text-sm">{{ t('projectPresentation.inProgress') }}</span>
            </label>

            <!-- En pause -->
            <label class="flex items-center cursor-pointer" [class.opacity-50]="isUpdatingStatus">
              <input type="radio" name="statut" class="custom-radio cursor-pointer"
                [checked]="isStatutSelected('En pause')" [disabled]="isUpdatingStatus"
                (change)="changeStatut('En pause')">
              <span class="ml-2 text-gray-700 font-bold text-sm">{{ t('status.pending') }}</span>
            </label>

            <!-- Terminé -->
            <label class="flex items-center cursor-pointer" [class.opacity-50]="isUpdatingStatus">
              <input type="radio" name="statut" class="custom-radio cursor-pointer"
                [checked]="isStatutSelected('Terminé')" [disabled]="isUpdatingStatus"
                (change)="changeStatut('Terminé')">
              <span class="ml-2 text-gray-700 font-bold text-sm">{{ t('projectPresentation.completed') }}</span>
            </label>
          </div>

          <!-- ✅ Message de confirmation de changement de statut -->
          <div *ngIf="statusSuccessMessage"
            class="mt-3 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700 animate-pulse">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-500 flex-shrink-0" viewBox="0 0 20 20"
              fill="currentColor">
              <path fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd" />
            </svg>
            <span class="font-medium">{{ statusSuccessMessage }}</span>
          </div>

          <!-- Statut actuel (visible en permanence) -->
          <div *ngIf="!isUpdatingStatus && projet && !statusSuccessMessage" class="mt-3 text-xs text-gray-500">
            {{ t('projectPresentation.currentStatus') }} : <span class="font-semibold text-gray-700">{{ statutFrancais
              }}</span>
          </div>

        </div>
      </div>
    </div>

    <div class="md:col-span-2">
      <div class="bg-white rounded shadow h-fit">
        <!-- En-tête avec titre et bouton -->
        <div class="flex justify-between items-center mb-6 p-4 border-b border-gray-200">
          <h1 class="text-2xl font-bold text-gray-800">{{ t('projectPresentation.generalInfo') }}</h1>
          <!-- <button (click)="onModifier()"
            class="px-4 py-2 bg-orange-500 text-white rounded-md flex items-center hover:bg-orange-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path
                d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Modifier
          </button> -->
        </div>

        <!-- Navigation par onglets -->
        <div class="border-b border-gray-200">
          <div class="flex justify-between items-center">
            <nav class="flex">
              <a (click)="setActiveTab('general')"
                class="flex items-center px-6 py-4 border-b-2 cursor-pointer transition-colors"
                [class]="isActiveTab('general') ? 'border-orange-500 text-orange-500 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 mr-2">
                  <path
                    d="M18.3337 18.3333H1.66699M1.66699 9.16667L5.05283 6.45833M18.3337 9.16667L11.562 3.75C11.1187 3.39541 10.568 3.20223 10.0003 3.20223C9.43268 3.20223 8.88193 3.39541 8.43866 3.75L7.78699 4.27083M12.917 4.58333V2.91667C12.917 2.80616 12.9609 2.70018 13.039 2.62204C13.1172 2.5439 13.2232 2.5 13.3337 2.5H15.417C15.5275 2.5 15.6335 2.5439 15.7116 2.62204C15.7898 2.70018 15.8337 2.80616 15.8337 2.91667V7.08333M3.33366 18.3333V7.91667M16.667 7.91667V11.25M16.667 18.3333V14.5833"
                    stroke="currentColor" stroke-width="1.25" stroke-linecap="round" />
                  <path
                    d="M12.5 18.3334V14.1667C12.5 12.9884 12.5 12.3992 12.1333 12.0334C11.7683 11.6667 11.1792 11.6667 10 11.6667C8.82083 11.6667 8.2325 11.6667 7.86667 12.0334M7.5 18.3334V14.1667"
                    stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M11.6663 7.91667C11.6663 8.35869 11.4907 8.78262 11.1782 9.09518C10.8656 9.40774 10.4417 9.58333 9.99967 9.58333C9.55765 9.58333 9.13372 9.40774 8.82116 9.09518C8.5086 8.78262 8.33301 8.35869 8.33301 7.91667C8.33301 7.47464 8.5086 7.05072 8.82116 6.73816C9.13372 6.4256 9.55765 6.25 9.99967 6.25C10.4417 6.25 10.8656 6.4256 11.1782 6.73816C11.4907 7.05072 11.6663 7.47464 11.6663 7.91667Z"
                    stroke="currentColor" stroke-width="1.25" />
                </svg>
                {{ t('projectPresentation.generalInfo') }}
              </a>
              <a (click)="setActiveTab('avancement')"
                class="flex items-center px-6 py-4 border-b-2 cursor-pointer transition-colors"
                [class]="isActiveTab('avancement') ? 'border-orange-500 text-orange-500 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 mr-2">
                  <path
                    d="M5.83333 13.3334C6.16485 13.3334 6.4828 13.2017 6.71722 12.9672C6.95164 12.7328 7.08333 12.4149 7.08333 12.0834C7.08646 12.0417 7.08646 12 7.08333 11.9584L9.40833 9.63335H9.79167L11.1333 10.975V11.0417C11.1333 11.3732 11.265 11.6911 11.4994 11.9256C11.7339 12.16 12.0518 12.2917 12.3833 12.2917C12.7149 12.2917 13.0328 12.16 13.2672 11.9256C13.5016 11.6911 13.6333 11.3732 13.6333 11.0417V10.975L16.6667 7.91669C16.9139 7.91669 17.1556 7.84338 17.3611 7.70602C17.5667 7.56867 17.7269 7.37345 17.8215 7.14504C17.9161 6.91663 17.9409 6.6653 17.8926 6.42282C17.8444 6.18035 17.7254 5.95762 17.5505 5.7828C17.3757 5.60799 17.153 5.48894 16.9105 5.44071C16.6681 5.39247 16.4167 5.41723 16.1883 5.51184C15.9599 5.60645 15.7647 5.76666 15.6273 5.97222C15.49 6.17779 15.4167 6.41946 15.4167 6.66669C15.4135 6.70829 15.4135 6.75008 15.4167 6.79169L12.4083 9.80002H12.275L10.8333 8.33335C10.8333 8.00183 10.7016 7.68389 10.4672 7.44947C10.2328 7.21505 9.91485 7.08335 9.58333 7.08335C9.25181 7.08335 8.93387 7.21505 8.69945 7.44947C8.46503 7.68389 8.33333 8.00183 8.33333 8.33335L5.83333 10.8334C5.50181 10.8334 5.18387 10.965 4.94945 11.1995C4.71503 11.4339 4.58333 11.7518 4.58333 12.0834C4.58333 12.4149 4.71503 12.7328 4.94945 12.9672C5.18387 13.2017 5.50181 13.3334 5.83333 13.3334ZM17.0833 16.6667H2.91667V2.50002C2.91667 2.27901 2.82887 2.06704 2.67259 1.91076C2.51631 1.75448 2.30435 1.66669 2.08333 1.66669C1.86232 1.66669 1.65036 1.75448 1.49408 1.91076C1.3378 2.06704 1.25 2.27901 1.25 2.50002V17.5C1.25 17.721 1.3378 17.933 1.49408 18.0893C1.65036 18.2456 1.86232 18.3334 2.08333 18.3334H17.0833C17.3043 18.3334 17.5163 18.2456 17.6726 18.0893C17.8289 17.933 17.9167 17.721 17.9167 17.5C17.9167 17.279 17.8289 17.067 17.6726 16.9108C17.5163 16.7545 17.3043 16.6667 17.0833 16.6667Z"
                    fill="currentColor" />
                </svg>
                {{ t('projectPresentation.progressState') }}
              </a>
              <a (click)="setActiveTab('budget')"
                class="flex items-center px-6 py-4 border-b-2 cursor-pointer transition-colors"
                [class]="isActiveTab('budget') ? 'border-orange-500 text-orange-500 font-medium' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 mr-2">
                  <path
                    d="M11.25 13.3333C11.25 13.6648 11.1183 13.9828 10.8839 14.2172C10.6495 14.4516 10.3315 14.5833 10 14.5833C9.66848 14.5833 9.35054 14.4516 9.11612 14.2172C8.8817 13.9828 8.75 13.6648 8.75 13.3333C8.75 13.0018 8.8817 12.6838 9.11612 12.4494C9.35054 12.215 9.66848 12.0833 10 12.0833C10.3315 12.0833 10.6495 12.215 10.8839 12.4494C11.1183 12.6838 11.25 13.0018 11.25 13.3333Z"
                    fill="currentColor" />
                  <path
                    d="M11.9558 0.549988L14.6058 4.26332L16.3533 3.66749L17.9483 8.33332H18.75V18.3333H1.25V8.33332H1.675V8.32499L2.215 8.32999L11.9558 0.549988ZM7.83083 8.33332H16.1875L15.3233 5.80582L14.055 6.21165L7.83083 8.33332ZM6.5325 7.01415L12.9583 4.82499L11.6217 2.94999L6.5325 7.01415ZM4.58333 9.99999H2.91667V11.6667C3.35869 11.6667 3.78262 11.4911 4.09518 11.1785C4.40774 10.8659 4.58333 10.442 4.58333 9.99999ZM12.9167 13.3333C12.9167 12.9503 12.8412 12.571 12.6946 12.2172C12.5481 11.8633 12.3332 11.5418 12.0624 11.2709C11.7916 11.0001 11.47 10.7852 11.1162 10.6387C10.7623 10.4921 10.383 10.4167 10 10.4167C9.61698 10.4167 9.23771 10.4921 8.88384 10.6387C8.52997 10.7852 8.20844 11.0001 7.93761 11.2709C7.66677 11.5418 7.45193 11.8633 7.30535 12.2172C7.15877 12.571 7.08333 12.9503 7.08333 13.3333C7.08333 14.1069 7.39062 14.8487 7.93761 15.3957C8.48459 15.9427 9.22645 16.25 10 16.25C10.7735 16.25 11.5154 15.9427 12.0624 15.3957C12.6094 14.8487 12.9167 14.1069 12.9167 13.3333ZM17.0833 16.6667V15C16.6413 15 16.2174 15.1756 15.9048 15.4881C15.5923 15.8007 15.4167 16.2246 15.4167 16.6667H17.0833ZM15.4167 9.99999C15.4167 10.442 15.5923 10.8659 15.9048 11.1785C16.2174 11.4911 16.6413 11.6667 17.0833 11.6667V9.99999H15.4167ZM2.91667 16.6667H4.58333C4.58333 16.2246 4.40774 15.8007 4.09518 15.4881C3.78262 15.1756 3.35869 15 2.91667 15V16.6667Z"
                    fill="currentColor" />
                </svg>
                {{ t('projectPresentation.budgetTab') }}
              </a>
            </nav>

            <!-- Bouton Ajouter (visible seulement sur État d'avancement) -->
            <div *ngIf="isActiveTab('avancement')" class="pr-6">
              <button (click)="triggerAddAlbum()"
                class="px-6 py-2.5 bg-orange-500 text-white rounded-lg flex items-center hover:bg-orange-600 transition-colors shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clip-rule="evenodd" />
                </svg>
                {{ t('projectPresentation.add') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Contenu des onglets -->
        <div class="p-6">
          <!-- Onglet Informations générales -->
          <div *ngIf="isActiveTab('general')">
            <!-- Titre et statut -->
            <div class="flex justify-between items-center mb-6">
              <h2 class="text-2xl font-bold text-gray-800">{{ projet.name }}</h2>
              <span class="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">{{ statutFrancais }}</span>
            </div>

            <!-- Description -->
            <p class="text-gray-600 mb-8">{{ projet.description }}</p>

            <!-- Montant -->
            <h3 class="text-3xl font-bold text-orange-500 mb-8">{{ projet.price | number }} F cfa</h3>

            <!-- Tableau d'informations -->
            <div class="grid grid-cols-1 md:grid-cols-2 mb-8 gap-5">
              <div class="flex justify-between border-b pb-2">
                <span class="text-gray-600">{{ t('projectPresentation.surface') }}</span>
                <span class="font-medium">{{ projet.area }} m²</span>
              </div>
              <div class="flex justify-between border-b pb-2">
                <span class="text-gray-600">{{ t('projectPresentation.location') }}</span>
                <span class="font-medium">{{ projet.address }}</span>
              </div>
              <div class="flex justify-between border-b pb-2">
                <span class="text-gray-600">{{ t('projectPresentation.numberOfLots') }}</span>
                <span class="font-medium">{{ projet.numberOfLots }}</span>
              </div>
              <div class="flex justify-between border-b pb-2">
                <span class="text-gray-600">{{ t('projectPresentation.dueDate') }}</span>
                <span class="font-medium">{{ dateFinPrevue }}</span>
              </div>
              <div class="flex justify-between border-b pb-2">
                <span class="text-gray-600">{{ t('projectPresentation.numberOfRooms') }}</span>
                <span class="font-medium">{{ projet.numberOfRooms }}</span>
              </div>
              <div class="justify-between border-b pb-2 hidden">
                <span class="text-gray-600">Numéro de projet</span>
                <!-- <span class="font-medium">{{ projet.number }}</span> -->
              </div>
            </div>

            <!-- Équipements communs -->
            <div class="mt-10">
              <h3 class="text-xl font-bold text-gray-800 mb-6">{{ t('projectPresentation.commonEquipment') }}</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div *ngFor="let equipement of equipementsCommuns" class="flex">
                  <div class="mr-4">
                    <img [src]="equipement.icon" [alt]="equipement.nom" class="w-8 h-8">
                  </div>
                  <div>
                    <h4 class="font-semibold text-gray-800">{{ equipement.nom }}</h4>
                    <p class="text-gray-600 text-sm">{{ equipement.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Onglet État d'avancement -->
          <div *ngIf="isActiveTab('avancement')">
            <app-status-report (progressUpdated)="onProgressUpdated()"></app-status-report>
          </div>

          <!-- Onglet Budget -->
          <div *ngIf="isActiveTab('budget')">
            <div class="space-y-6">
              <h3 class="text-lg font-semibold text-gray-700">{{ t('projectPresentation.budgetInfo') }}</h3>
              <app-project-budget></app-project-budget>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>